<template>
  <component
    :is="layoutComponent"
    page-title="Tank Sheet"
    :page-subtitle="form.well_name || 'Daily tank and meter record'"
  >
    <div class="tank-sheet-page">
      <div class="top-actions no-print">
        <a href="#" class="back-link" @click.prevent="goBack">← Back to Tank Sheets</a>

        <div class="action-group">
          <button class="btn btn-secondary" type="button" @click="printSheet">Print</button>
          <button class="btn btn-danger" type="button" :disabled="deleting" @click="deleteSheet">
            {{ deleting ? 'Deleting…' : 'Delete' }}
          </button>
          <button class="btn btn-primary" type="button" :disabled="saving" @click="saveSheet">
            {{ saving ? 'Saving…' : 'Save' }}
          </button>
        </div>
      </div>

      <div v-if="saved" class="status status-success no-print">Tank sheet saved.</div>
      <div v-if="errorMessage" class="status status-error no-print">{{ errorMessage }}</div>
      <div v-if="loading" class="status no-print">Loading sheet…</div>

      <section v-else class="sheet-card">
        <div class="sheet-top">
          <div v-if="!isEmployee" class="brand-upload no-print">
            <input
              ref="logoInput"
              id="company-logo"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              class="sr-only"
              @change="onLogoChange"
            />
            <label class="logo-box" for="company-logo">
              <img v-if="logoPreview" :src="logoPreview" alt="Company logo preview" />
              <div v-else class="logo-placeholder">
                <strong>Company Logo</strong>
                <span>Click to upload</span>
              </div>
            </label>
            <button v-if="logoPreview" type="button" class="clear-logo" @click="clearLogo">
              Remove logo
            </button>
          </div>

          <div v-else-if="logoPreview" class="brand-upload no-print">
            <div class="logo-box"><img :src="logoPreview" alt="Company logo" /></div>
          </div>

          <div v-if="logoPreview" class="print-logo print-only">
            <img :src="logoPreview" alt="Company logo" />
          </div>

          <div class="sheet-title">
            <h1>Tank Sheet</h1>
            <p>Daily tank and meter record</p>
          </div>
        </div>

        <div class="meta-grid">
          <label>
            <span>Customer</span>
            <input v-model="form.customer" type="text" placeholder="Customer" />
          </label>
          <label>
            <span>I/R</span>
            <input v-model="form.ir" type="text" placeholder="I/R" />
          </label>
          <label>
            <span>Operator</span>
            <input v-model="form.operator" type="text" placeholder="Operator" />
          </label>
          <label>
            <span>Date</span>
            <input v-model="form.date" type="date" />
          </label>
        </div>

        <div class="well-line">
          <span class="well-label">Well / Location</span>
          <input v-model="form.well_name" type="text" placeholder="Well name / location" />
          <span class="trucked-label">Trucked Out</span>
        </div>

        <div class="table-scroll">
          <table class="tank-grid">
            <thead>
              <tr>
                <th rowspan="2" class="row-num">#</th>
                <th>Water<br />Meter<br />Total</th>
                <th>Water<br />Meter<br />Rate</th>
                <th>Oil<br />Meter<br />Total</th>
                <th>Oil<br />Meter<br />Rate</th>
                <th>Tank 1<br />Strap &amp;<br />Volume</th>
                <th>Tank 2<br />Strap &amp;<br />Volume</th>
                <th>Tank 3<br />Strap &amp;<br />Volume</th>
                <th>Water<br />Gain</th>
                <th>Oil Tank<br />Strap &amp;<br />Volume</th>
                <th>Oil<br />Gain</th>
                <th colspan="2" class="trucked-out-head">Trucked Out</th>
              </tr>
              <tr class="sub-head">
                <th colspan="10"></th>
                <th>Ticket #</th>
                <th>Hauled</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, rowIndex) in form.rows" :key="rowIndex">
                <td class="row-num">{{ rowIndex + 1 }}</td>

                <td><input v-model="row.water_meter_total" data-sheet-cell="true" @keydown="moveCell" /></td>
                <td><input v-model="row.water_meter_rate" data-sheet-cell="true" @keydown="moveCell" /></td>
                <td><input v-model="row.oil_meter_total" data-sheet-cell="true" @keydown="moveCell" /></td>
                <td><input v-model="row.oil_meter_rate" data-sheet-cell="true" @keydown="moveCell" /></td>

                <td class="split-cell">
                  <input v-model="row.tank_1_strap" data-sheet-cell="true" class="split-top" @keydown="moveCell" />
                  <span class="diag"></span>
                  <input v-model="row.tank_1_volume" data-sheet-cell="true" class="split-bottom" @keydown="moveCell" />
                </td>

                <td class="split-cell">
                  <input v-model="row.tank_2_strap" data-sheet-cell="true" class="split-top" @keydown="moveCell" />
                  <span class="diag"></span>
                  <input v-model="row.tank_2_volume" data-sheet-cell="true" class="split-bottom" @keydown="moveCell" />
                </td>

                <td class="split-cell">
                  <input v-model="row.tank_3_strap" data-sheet-cell="true" class="split-top" @keydown="moveCell" />
                  <span class="diag"></span>
                  <input v-model="row.tank_3_volume" data-sheet-cell="true" class="split-bottom" @keydown="moveCell" />
                </td>

                <td><input v-model="row.water_gain" data-sheet-cell="true" @keydown="moveCell" /></td>

                <td class="split-cell">
                  <input v-model="row.oil_tank_strap" data-sheet-cell="true" class="split-top" @keydown="moveCell" />
                  <span class="diag"></span>
                  <input v-model="row.oil_tank_volume" data-sheet-cell="true" class="split-bottom" @keydown="moveCell" />
                </td>

                <td><input v-model="row.oil_gain" data-sheet-cell="true" @keydown="moveCell" /></td>
                <td><input v-model="row.ticket_number" data-sheet-cell="true" @keydown="moveCell" /></td>
                <td><input v-model="row.hauled" data-sheet-cell="true" @keydown="moveCell" /></td>
              </tr>
            </tbody>
          </table>
        </div>

        <p class="keyboard-help no-print">
          Arrow keys move between cells. Enter moves down. Tab moves right. Delete clears a cell.
        </p>
      </section>
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'
import AppLayout from '@/components/layout/AppLayout.vue'

const route = useRoute()
const router = useRouter()
const sheetId = String(route.params.id)
const isEmployee = computed(() => !route.path.startsWith('/company'))
const fieldHome = computed(() => route.path.startsWith('/employee') ? '/employee' : '')
const layoutComponent = computed(() => isEmployee.value ? AppLayout : CompanyAppLayout)
const apiBase = computed(() => `/${isEmployee.value ? 'api/employee' : 'api/company'}/tank-sheets`)

const logoInput = ref<HTMLInputElement | null>(null)
const logoPreview = ref<string | null>(null)
const logoFile = ref<File | null>(null)
const loading = ref(false)
const saving = ref(false)
const deleting = ref(false)
const saved = ref(false)
const errorMessage = ref('')
let blobUrl: string | null = null

const blankRow = () => ({
  water_meter_total: '',
  water_meter_rate: '',
  oil_meter_total: '',
  oil_meter_rate: '',
  tank_1_strap: '',
  tank_1_volume: '',
  tank_2_strap: '',
  tank_2_volume: '',
  tank_3_strap: '',
  tank_3_volume: '',
  water_gain: '',
  oil_tank_strap: '',
  oil_tank_volume: '',
  oil_gain: '',
  ticket_number: '',
  hauled: '',
})

const form = reactive({
  customer: '',
  ir: '',
  operator: '',
  date: new Date().toISOString().slice(0, 10),
  well_name: '',
  rows: Array.from({ length: 14 }, blankRow) as Record<string, string>[],
})

function authHeaders(json = true) {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}`,
  }
  if (json) headers['Content-Type'] = 'application/json'
  return headers
}

function onLogoChange(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0]
  if (!file) return
  logoFile.value = file
  if (blobUrl) URL.revokeObjectURL(blobUrl)
  blobUrl = URL.createObjectURL(file)
  logoPreview.value = blobUrl
}

function clearLogo() {
  logoFile.value = null
  if (blobUrl) URL.revokeObjectURL(blobUrl)
  blobUrl = null
  logoPreview.value = null
  if (logoInput.value) logoInput.value.value = ''
}

function moveCell(event: KeyboardEvent) {
  const key = event.key
  const cells = [...document.querySelectorAll<HTMLInputElement>('[data-sheet-cell="true"]')]
  const current = event.currentTarget as HTMLInputElement
  const idx = cells.indexOf(current)
  const cols = 16

  let targetIndex: number | null = null
  if (key === 'ArrowRight' || key === 'Tab') targetIndex = idx + 1
  if (key === 'ArrowLeft') targetIndex = idx - 1
  if (key === 'ArrowDown' || key === 'Enter') targetIndex = idx + cols
  if (key === 'ArrowUp') targetIndex = idx - cols

  if (key === 'Delete') {
    event.preventDefault()
    current.value = ''
    current.dispatchEvent(new Event('input'))
    return
  }

  if (targetIndex !== null && cells[targetIndex]) {
    event.preventDefault()
    cells[targetIndex].focus()
    cells[targetIndex].select?.()
  }
}

async function load() {
  loading.value = true
  errorMessage.value = ''
  try {
    const res = await fetch(`${apiBase.value}/${sheetId}`, { headers: authHeaders() })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const { sheet } = await res.json()
    form.customer = sheet.customer || ''
    form.ir = sheet.ir || ''
    form.operator = sheet.operator || ''
    form.date = sheet.date || new Date().toISOString().slice(0, 10)
    form.well_name = sheet.well_name || ''
    form.rows = (sheet.rows || []).map((r: Record<string, string>) => ({ ...blankRow(), ...r }))
    while (form.rows.length < 14) form.rows.push(blankRow())
    logoPreview.value = sheet.company_logo_url || sheet.company_logo_path || null
  } catch (e: any) {
    errorMessage.value = e.message || 'Failed to load tank sheet'
  } finally {
    loading.value = false
  }
}

async function saveSheet() {
  saving.value = true
  saved.value = false
  errorMessage.value = ''

  try {
    const data = new FormData()
    data.append('customer', form.customer)
    data.append('ir', form.ir)
    data.append('operator', form.operator)
    data.append('date', form.date)
    data.append('well_name', form.well_name)
    data.append('rows', JSON.stringify(form.rows))
    if (logoFile.value) data.append('company_logo', logoFile.value)

    const res = await fetch(`${apiBase.value}/${sheetId}`, {
      method: 'POST',
      headers: authHeaders(isEmployee.value),
      body: isEmployee.value
        ? JSON.stringify({ customer: form.customer, ir: form.ir, operator: form.operator, date: form.date, well_name: form.well_name, rows: form.rows })
        : data,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.message || err.error || `HTTP ${res.status}`)
    }

    const result = await res.json().catch(() => ({}))
    if (result.logoPath || result.company_logo_url) {
      logoPreview.value = result.company_logo_url || result.logoPath
      logoFile.value = null
    }

    saved.value = true
    setTimeout(() => (saved.value = false), 2200)
  } catch (e: any) {
    errorMessage.value = e.message || 'The tank sheet could not be saved.'
  } finally {
    saving.value = false
  }
}

async function deleteSheet() {
  if (!window.confirm('Delete this tank sheet? This cannot be undone.')) return
  deleting.value = true
  errorMessage.value = ''
  try {
    const res = await fetch(`${apiBase.value}/${sheetId}`, {
      method: 'DELETE',
      headers: authHeaders(),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
    goBack()
  } catch (e: any) {
    errorMessage.value = e.message || 'Delete failed'
  } finally {
    deleting.value = false
  }
}

function printSheet() {
  window.print()
}

function goBack() {
  router.push(isEmployee.value ? `${fieldHome.value}/tank-sheets` || '/tank-sheets' : '/company/tank-sheets')
}

onMounted(load)
onBeforeUnmount(() => {
  if (blobUrl) URL.revokeObjectURL(blobUrl)
})
</script>

<style scoped>
.tank-sheet-page {
  padding: 18px 0 32px;
  color: #d9e6f2;
}

.top-actions {
  max-width: 1510px;
  margin: 0 auto 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.back-link {
  color: #8fb8dc;
  text-decoration: none;
  font-size: 14px;
}

.action-group {
  display: flex;
  gap: 10px;
}

.btn {
  border: 1px solid #3b4d64;
  border-radius: 8px;
  padding: 9px 16px;
  font-weight: 700;
  cursor: pointer;
}

.btn-secondary {
  background: #26374d;
  color: #e9f1f7;
}

.btn-primary {
  border-color: #0e8a72;
  background: #087864;
  color: #fff;
}

.btn-danger {
  border-color: #8f3c49;
  background: #411b23;
  color: #ffc4cc;
}

.btn:disabled {
  opacity: .6;
  cursor: wait;
}

.status {
  max-width: 1510px;
  margin: 0 auto 12px;
  border-radius: 8px;
  padding: 10px 14px;
  font-size: 14px;
}

.status-success {
  border: 1px solid #177b68;
  background: #0d3e36;
  color: #7ee3cf;
}

.status-error {
  border: 1px solid #8f3c49;
  background: #411b23;
  color: #ffc4cc;
}

.sheet-card {
  max-width: 1510px;
  margin: 0 auto;
  overflow: hidden;
  border: 1px solid #344960;
  border-radius: 10px;
  background: #e8eceb;
  color: #384854;
  box-shadow: 0 18px 50px rgba(0,0,0,.24);
}

.sheet-top {
  position: relative;
  min-height: 108px;
  border-bottom: 3px double #758896;
  background:
    linear-gradient(rgba(255,255,255,.35), rgba(255,255,255,.35)),
    #dce2e1;
}

.brand-upload {
  position: absolute;
  left: 14px;
  top: 10px;
  width: 230px;
}

.logo-box {
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #6f8492;
  border-radius: 6px;
  background: rgba(255,255,255,.36);
  cursor: pointer;
  overflow: hidden;
}

.logo-box img,
.print-logo img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.logo-placeholder {
  display: grid;
  gap: 3px;
  text-align: center;
  color: #536b79;
  font-size: 12px;
}

.logo-placeholder strong {
  font-size: 14px;
}

.clear-logo {
  margin-top: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #9a3f46;
  cursor: pointer;
  font-size: 11px;
}

.sheet-title {
  display: grid;
  place-items: center;
  padding-top: 26px;
}

.sheet-title h1 {
  margin: 0;
  letter-spacing: .08em;
  text-transform: uppercase;
  font-size: 28px;
  color: #425563;
}

.sheet-title p {
  margin: 5px 0 0;
  color: #6d808d;
  font-size: 12px;
}

.meta-grid {
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr 1fr;
  border-bottom: 2px solid #788b98;
}

.meta-grid label {
  display: grid;
  grid-template-columns: max-content 1fr;
  align-items: center;
  gap: 8px;
  min-height: 42px;
  padding: 0 10px;
  border-right: 1px solid #8b9ba5;
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
}

.meta-grid label:last-child {
  border-right: 0;
}

.meta-grid input,
.well-line input {
  min-width: 0;
  border: 0;
  border-bottom: 1px solid #748895;
  background: transparent;
  color: #293a45;
  outline: none;
  padding: 7px 4px 4px;
  font-size: 14px;
  font-weight: 650;
}

.well-line {
  display: grid;
  grid-template-columns: max-content 1fr max-content;
  align-items: center;
  gap: 10px;
  min-height: 40px;
  padding: 0 10px;
  border-bottom: 2px solid #788b98;
  background: #dce2e1;
}

.well-label,
.trucked-label {
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
}

.trucked-label {
  padding: 0 14px;
}

.table-scroll {
  overflow-x: auto;
}

.tank-grid {
  width: 100%;
  min-width: 1280px;
  border-collapse: collapse;
  table-layout: fixed;
  background: #e8eceb;
  font-size: 12px;
}

.tank-grid th,
.tank-grid td {
  border-right: 2px solid #81939e;
  border-bottom: 2px solid #81939e;
}

.tank-grid th:last-child,
.tank-grid td:last-child {
  border-right: 0;
}

.tank-grid thead th {
  height: 70px;
  padding: 4px;
  background: #cfd8da;
  color: #516674;
  text-align: center;
  vertical-align: middle;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.12;
}

.tank-grid thead .sub-head th {
  height: 25px;
  padding: 2px 4px;
}

.trucked-out-head {
  background: #c8d2d5 !important;
  text-transform: uppercase;
  letter-spacing: .02em;
}

.row-num {
  width: 36px;
  text-align: center;
  color: #5d6e79;
  font-weight: 800;
}

.tank-grid tbody tr {
  height: 56px;
}

.tank-grid td {
  position: relative;
  padding: 0;
  background: rgba(255,255,255,.12);
}

.tank-grid td > input {
  width: 100%;
  height: 54px;
  box-sizing: border-box;
  border: 0;
  background: transparent;
  color: #354650;
  outline: none;
  padding: 6px 8px;
  font: 650 14px/1.1 "Segoe UI", sans-serif;
  text-align: center;
}

.tank-grid td > input:focus {
  background: rgba(23, 172, 145, .10);
  box-shadow: inset 0 0 0 2px #13a68c;
}

.split-cell {
  height: 54px;
  overflow: hidden;
}

.split-cell .diag {
  position: absolute;
  inset: 0;
  pointer-events: none;
}

.split-cell .diag::after {
  content: "";
  position: absolute;
  left: -8%;
  top: 50%;
  width: 116%;
  height: 1.5px;
  background: #718792;
  transform: rotate(-29deg);
  transform-origin: center;
}

.split-cell > input {
  position: absolute;
  z-index: 2;
  width: 58% !important;
  height: 27px !important;
  padding: 2px 5px !important;
  background: transparent !important;
  text-align: left !important;
}

.split-cell .split-top {
  left: 4px;
  top: 1px;
}

.split-cell .split-bottom {
  right: 4px;
  bottom: 1px;
  text-align: right !important;
}

.keyboard-help {
  margin: 0;
  padding: 9px 12px;
  background: #101f31;
  color: #7893ad;
  font-size: 12px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0,0,0,0);
  white-space: nowrap;
  border: 0;
}

.print-only {
  display: none;
}

@media (max-width: 1000px) {
  .meta-grid {
    grid-template-columns: 1fr 1fr;
  }

  .brand-upload {
    position: static;
    width: auto;
    padding: 12px;
  }

  .sheet-top {
    min-height: 0;
  }

  .sheet-title {
    padding: 8px 12px 18px;
  }
}

@media print {
  .tank-sheet-page {
    padding: 0;
    color: #000;
  }

  .no-print {
    display: none !important;
  }

  .print-only {
    display: block;
  }

  .sheet-card {
    max-width: none;
    border: 0;
    border-radius: 0;
    box-shadow: none;
  }

  .print-logo {
    position: absolute;
    left: 14px;
    top: 12px;
    width: 220px;
    height: 72px;
  }

  .table-scroll {
    overflow: visible;
  }

  .tank-grid {
    min-width: 0;
    font-size: 9px;
  }

  .tank-grid thead th {
    font-size: 9px;
  }

  .tank-grid td > input {
    font-size: 10px;
  }

  @page {
    size: landscape;
    margin: 8mm;
  }
}
</style>
